import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/core/supabaseClient';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';

/**
 * Supabase session lifecycle, email/password auth handlers, and cached PRO entitlement from `users`.
 */
export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isPro, setIsPro] = useState(false);

  const fetchProStatus = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('is_pro')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST205') {
          console.warn('Pro status: users table missing or not in schema (PGRST205); treating as non-Pro');
        } else {
          console.error('Error fetching pro status:', error);
        }
        setIsPro(false);
        return;
      }
      setIsPro(Boolean(data?.is_pro));
    } catch (e) {
      console.error('Unexpected error fetching pro status:', e);
      setIsPro(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth
      .getSession()
      .then(({ data: { session: currentSession } }: { data: { session: Session | null } }) => {
        if (!mounted) return;
        try {
          setSession(currentSession);
          const currentUser = currentSession?.user ?? null;
          setUser(currentUser);
          console.log('Auth Debug:', { session: !!currentSession, user: !!currentUser });

          setAuthLoading(false);

          if (currentUser) {
            void fetchProStatus(currentUser.id);
          } else {
            setIsPro(false);
          }
        } catch (e) {
          console.error('Auth initial session error:', e);
          if (mounted) {
            setIsPro(false);
            setAuthLoading(false);
          }
        }
      })
      .catch((e) => {
        console.error('Auth getSession failed:', e);
        if (mounted) setAuthLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      if (!mounted) return;
      try {
        setSession(session);
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        console.log('Auth Debug:', { session: !!session, user: !!currentUser });

        setAuthLoading(false);

        if (currentUser) {
          void fetchProStatus(currentUser.id);
        } else {
          setIsPro(false);
        }
      } catch (e) {
        console.error('Auth state change error:', e);
        if (mounted) {
          setIsPro(false);
          setAuthLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProStatus]);

  const handleSignUp = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
      toast.success('Sprawdź swój email, aby zweryfikować konto!');
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [email, password]);

  const handleLogin = useCallback(async (): Promise<boolean> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return true;
    } catch (error: any) {
      toast.error(error.message);
      return false;
    }
  }, [email, password]);

  const handleLogout = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return {
    session,
    user,
    authLoading,
    email,
    setEmail,
    password,
    setPassword,
    isSigningUp,
    setIsSigningUp,
    handleSignUp,
    handleLogin,
    handleLogout,
    isPro,
  };
}
